import React, { useState } from 'react';
import {
  AppBar,
  Box,
  CssBaseline,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  Avatar,
  Divider,
  Tooltip,
  Chip,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Event as EventIcon,
  MedicalServices as MedicalIcon,
  Notifications as NotificationsIcon,
  Receipt as BillingIcon,
  LocalPharmacy as PharmacyIcon,
  Assessment as ReportIcon,
  Logout as LogoutIcon,
} from '@mui/icons-material';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';

const drawerWidth = 280;

interface LayoutProps {
  children: React.ReactNode;
}

interface MenuItem {
  text: string;
  icon: React.ReactNode;
  path: string;
  roles: string[];
}

const menuItems: MenuItem[] = [
  { text: 'Dashboard', icon: <DashboardIcon />, path: '/dashboard', roles: ['Admin', 'Reception', 'Pharmacy'] },
  { text: 'Patients', icon: <PeopleIcon />, path: '/patients', roles: ['Admin', 'Reception', 'Pharmacy'] },
  { text: 'My Appointments', icon: <EventIcon />, path: '/appointments', roles: ['Admin', 'Reception'] },
  { text: 'Billing', icon: <BillingIcon />, path: '/billing', roles: ['Admin', 'Reception', 'Pharmacy'] },
  { text: 'Pharmacy', icon: <PharmacyIcon />, path: '/pharmacy', roles: ['Admin', 'Pharmacy'] },
  { text: 'Medical History', icon: <MedicalIcon />, path: '/medical-history', roles: ['Admin', 'Reception', 'Pharmacy'] },
  { text: 'Reports', icon: <ReportIcon />, path: '/reports', roles: ['Admin', 'Reception', 'Pharmacy'] },
];

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  // Filter menu items based on user role
  const filteredMenuItems = menuItems.filter(item => 
    user && item.roles.includes(user.role)
  );

  const displayMenuItems = filteredMenuItems.length > 0 ? filteredMenuItems : menuItems;

  const getUserRoleColor = (role: string) => {
    switch (role) {
      case 'Admin': return '#ef4444';
      case 'Reception': return '#10b981';
      case 'Pharmacy': return '#8b5cf6';
      default: return '#3b82f6';
    }
  };

  const drawer = (
    <Box sx={{ 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      background: 'linear-gradient(180deg, #2C7A9C 0%, #1E5A73 50%, #134152 100%)',
    }}>
      {/* Logo Section */}
      <Toolbar sx={{ px: 3, py: 3 }}>
        <Box display="flex" alignItems="center" gap={2}>
          <Box
            sx={{
              width: 52,
              height: 52,
              borderRadius: '50%',
              background: 'white',
              boxShadow: '0 4px 20px rgba(0, 0, 0, 0.2)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '4px',
            }}
          >
            <img 
              src="/images/plasticare-logo.png" 
              alt="Plasticare Logo" 
              style={{ 
                width: '100%', 
                height: '100%', 
                objectFit: 'contain',
                borderRadius: '50%'
              }} 
            />
          </Box>
          <Box>
            <Typography 
              variant="h6" 
              sx={{ 
                fontWeight: 800,
                fontSize: '1.2rem',
                letterSpacing: '-0.02em',
                color: 'white',
                lineHeight: 1.2,
              }}
            >
              <span style={{ color: '#8B4513' }}>SCULPTE</span>
              <span style={{ fontSize: '0.7em', fontWeight: 400 }}> by </span>
              <span>Plasticare</span>
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'rgba(255,255,255,0.8)', 
                fontSize: '0.75rem',
                fontWeight: 500,
              }}
            >
              Hospital Management
            </Typography>
          </Box>
        </Box>
      </Toolbar>
      
      <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', mx: 2 }} />
      
      {/* Navigation List */}
      <List sx={{ px: 2, py: 3, flexGrow: 1 }}>
        {displayMenuItems.map((item, index) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem 
              key={item.text} 
              disablePadding 
              sx={{ mb: 1 }}
            >
              <ListItemButton
                selected={isActive}
                onClick={() => navigate(item.path)}
                sx={{
                  borderRadius: 3,
                  py: 1.5,
                  px: 2.5,
                  transition: 'all 0.2s ease',
                  backgroundColor: isActive ? 'rgba(255,255,255,0.15)' : 'transparent',
                  borderLeft: isActive ? '4px solid white' : '4px solid transparent',
                  '&:hover': {
                    backgroundColor: 'rgba(255,255,255,0.1)',
                    transform: 'translateX(4px)',
                  },
                  '& .MuiListItemIcon-root': {
                    color: 'rgba(255,255,255,0.9)',
                    minWidth: 44,
                  },
                  '& .MuiListItemText-primary': {
                    color: 'white',
                    fontWeight: isActive ? 600 : 500,
                    fontSize: '0.95rem',
                  },
                }}
              >
                <ListItemIcon>{item.icon}</ListItemIcon>
                <ListItemText primary={item.text} />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>
      
      {/* User Section */}
      <Box sx={{ p: 2 }}>
        <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', mb: 2 }} />
        <Box 
          sx={{ 
            display: 'flex',
            alignItems: 'center', 
            gap: 1.5,
            p: 1.5,
            borderRadius: 3,
            backgroundColor: 'rgba(255,255,255,0.1)',
          }}
        >
          <Avatar 
            sx={{ 
              width: 40, 
              height: 40,
              background: 'rgba(255,255,255,0.2)',
              color: 'white',
              fontWeight: 600,
              fontSize: '0.9rem',
            }}
          >
            {user ? `${user.firstName?.[0] || ''}${user.lastName?.[0] || ''}` : 'U'}
          </Avatar>
          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography 
              variant="body2" 
              sx={{ 
                color: 'white', 
                fontWeight: 600,
                fontSize: '0.85rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {user ? `${user.firstName || ''} ${user.lastName || ''}` : 'User'}
            </Typography>
            <Chip
              label={user?.role || 'User'}
              size="small"
              sx={{
                bgcolor: getUserRoleColor(user?.role || 'User'),
                color: 'white',
                fontSize: '0.65rem',
                height: '18px',
                fontWeight: 600,
                '& .MuiChip-label': { px: 1 },
              }}
            />
          </Box>
          <Tooltip title="Logout">
            <IconButton 
              size="small" 
              onClick={handleLogout}
              sx={{ 
                color: 'rgba(255,255,255,0.8)',
                '&:hover': {
                  backgroundColor: 'rgba(255, 100, 100, 0.2)',
                  color: '#fca5a5',
                },
              }}
            >
              <LogoutIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      
      {/* App Bar */}
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          ml: { sm: `${drawerWidth}px` },
          background: 'white',
          borderBottom: '1px solid #e0f2f1',
        }}
      >
        <Toolbar sx={{ px: 3 }}>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ 
              mr: 2, 
              display: { sm: 'none' },
              color: '#2C7A9C',
            }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ flexGrow: 1 }}>
            <Typography 
              variant="h6" 
              noWrap 
              sx={{ 
                fontWeight: 700,
                fontSize: '1.4rem',
                color: '#2C7A9C',
              }}
            >
              SCULPTE Platform
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: '#4a6a6a',
                fontSize: '0.8rem',
              }}
            >
              Welcome back! Here's what's happening today.
            </Typography>
          </Box>
          
          <Box display="flex" alignItems="center" gap={1}>
            <Tooltip title="Notifications">
              <IconButton 
                sx={{
                  color: '#2C7A9C',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 128, 128, 0.08)',
                  },
                }}
              >
                <NotificationsIcon />
              </IconButton>
            </Tooltip>
            
            <Avatar 
              sx={{ 
                width: 40, 
                height: 40,
                background: 'linear-gradient(135deg, #2C7A9C 0%, #4A9BBD 100%)',
                ml: 1,
                fontWeight: 600,
                fontSize: '0.85rem',
              }}
            >
              {user?.firstName?.charAt(0) || 'U'}{user?.lastName?.charAt(0) || 'S'}
            </Avatar>
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Drawer */}
      <Box
        component="nav"
        sx={{ width: { sm: drawerWidth }, flexShrink: { sm: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
            },
          }}
        >
          {drawer}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              border: 'none',
              borderRadius: '0 24px 24px 0',
              boxShadow: '4px 0 24px rgba(0, 128, 128, 0.1)',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>
      
      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${drawerWidth}px)` },
          background: '#f8fffe',
          minHeight: '100vh',
        }}
      >
        <Toolbar />
        <Box>
          {children}
        </Box>
      </Box>
    </Box>
  );
};

export default Layout;
